import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, Download, Heart, MessageCircle, Presentation } from "lucide-react";
import { toast } from "sonner";
import {
  getPublishedPresentation,
  getPresentationEngagement,
  togglePresentationLike,
  addPresentationComment,
} from "@/lib/api/presentations.functions";
import { FacebookShareButton } from "@/components/site/FacebookShareButton";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const SITE = "https://nkadimengdannylcs.com";

export const Route = createFileRoute("/presentations/$slug")({
  loader: async ({ params }) => {
    const res = await getPublishedPresentation({ data: { slug: params.slug } });
    if (!res.presentation) throw notFound();
    return res;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData?.presentation) {
      return {
        meta: [{ title: "Presentation unavailable" }, { name: "robots", content: "noindex" }],
      };
    }
    const p = loaderData.presentation as any;
    const url = `${SITE}/presentations/${params.slug}`;
    const desc = (p.summary || `A law class presentation: ${p.title}.`).slice(0, 155);
    const meta: Record<string, string>[] = [
      { title: `${p.title} | Class Presentations` },
      { name: "description", content: desc },
      { property: "og:title", content: p.title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
    ];
    if (p.cover_image_url && /^https:\/\//.test(p.cover_image_url)) {
      const v = p.published_at ? new Date(p.published_at).getTime() : 0;
      const img = `${p.cover_image_url}${p.cover_image_url.includes("?") ? "&" : "?"}v=${v}`;
      meta.push({ property: "og:image", content: img });
      meta.push({ name: "twitter:image", content: img });
    }
    return { meta, links: [{ rel: "canonical", href: url }] };
  },
  component: PresentationPage,
  notFoundComponent: () => (
    <div className="container-narrow py-16">
      <h1 className="font-display text-2xl text-navy">Presentation not found</h1>
      <Link to="/presentations" className="mt-2 inline-block text-navy hover:underline">
        Back to presentations
      </Link>
    </div>
  ),
});

function PresentationPage() {
  const { presentation } = Route.useLoaderData();
  const params = Route.useParams();
  const p = presentation as any;
  const url = `${SITE}/presentations/${params.slug}`;

  const engagementFn = useServerFn(getPresentationEngagement);
  const likeFn = useServerFn(togglePresentationLike);
  const commentFn = useServerFn(addPresentationComment);
  const qc = useQueryClient();
  const [comment, setComment] = useState("");

  const eng = useQuery({
    queryKey: ["presentation-engagement", p.id],
    queryFn: () => engagementFn({ data: { presentation_id: p.id } }),
    retry: false,
  });
  const signedIn = !eng.isError;

  const caption = [p.title, p.summary, `Read more: ${url}`].filter(Boolean).join("\n\n");

  return (
    <article className="container-narrow py-12">
      <Link to="/presentations" className="inline-flex items-center gap-1 text-sm text-navy hover:underline">
        <ArrowLeft className="h-4 w-4" /> All presentations
      </Link>

      <header className="mt-4">
        {p.course_label && (
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{p.course_label}</p>
        )}
        <h1 className="mt-1 font-display text-3xl text-navy">{p.title}</h1>
        {p.summary && <p className="mt-2 text-muted-foreground">{p.summary}</p>}
      </header>

      {p.cover_image_url && (
        <img
          src={p.cover_image_url}
          alt={p.title}
          width={1200}
          height={630}
          className="mt-6 aspect-video w-full rounded-lg object-cover"
        />
      )}

      {p.embed_url && (
        <div className="mt-6 aspect-video w-full overflow-hidden rounded-lg border border-border">
          <iframe
            src={p.embed_url}
            title={p.title}
            allowFullScreen
            loading="lazy"
            className="h-full w-full"
          />
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <FacebookShareButton url={url} caption={caption} variant="full" itemSlug={params.slug} />
        {p.allow_download && p.file_url && (
          <a
            href={p.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:border-navy"
          >
            <Download className="h-4 w-4" /> Download slides
          </a>
        )}
        {signedIn && (
          <button
            type="button"
            onClick={async () => {
              await likeFn({ data: { presentation_id: p.id } });
              qc.invalidateQueries({ queryKey: ["presentation-engagement", p.id] });
            }}
            className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:border-navy"
          >
            <Heart className={eng.data?.liked ? "h-4 w-4 fill-gold text-gold" : "h-4 w-4"} />
            {eng.data?.likeCount ?? 0}
          </button>
        )}
      </div>

      {p.speaker_notes && (
        <section className="mt-8 rounded-lg border border-border bg-card p-5">
          <h2 className="font-display text-lg text-navy">Speaker notes</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/85">{p.speaker_notes}</p>
        </section>
      )}

      <section className="mt-8">
        <h2 className="flex items-center gap-2 font-display text-lg text-navy">
          <MessageCircle className="h-4 w-4 text-gold" /> Class discussion
        </h2>
        {signedIn ? (
          <div className="mt-3 space-y-2">
            <Textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share feedback with your classmate…"
              aria-label="Add a comment"
            />
            <Button
              type="button"
              className="bg-navy hover:bg-navy/90"
              disabled={comment.trim().length < 2}
              onClick={async () => {
                try {
                  await commentFn({ data: { presentation_id: p.id, body: comment.trim() } });
                  setComment("");
                  qc.invalidateQueries({ queryKey: ["presentation-engagement", p.id] });
                } catch (err: any) {
                  toast.error("Could not post", { description: err?.message });
                }
              }}
            >
              Post comment
            </Button>
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            <Link to={"/auth" as any} className="text-navy hover:underline">
              Sign in
            </Link>{" "}
            to like and comment on this presentation.
          </p>
        )}

        <ul className="mt-4 space-y-3">
          {(eng.data?.comments ?? []).map((c: any) => (
            <li key={c.id} className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs text-muted-foreground">
                {c.author_name} · {new Date(c.created_at).toLocaleString()}
              </p>
              <p className="mt-1 text-sm text-foreground/90">{c.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-10 flex items-center gap-2 text-xs text-muted-foreground">
        <Presentation className="h-3.5 w-3.5" /> Published by a student of Nkadimeng Danny Legal &
        Compliance Services.
      </p>
    </article>
  );
}
